import { supabase } from '@/integrations/supabase/client';
import type { ToolConfig } from '@/lib/llm/providerRegistry';

export interface RegisteredModel {
  id?: string;
  provider: string;
  model_id: string;
  display_name: string;
  supports_reasoning: boolean;
  supports_images: boolean;
  supports_video: boolean;
  supports_audio: boolean;
  tools: ToolConfig;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

const MODELS_API_URL = '/api/chat-settings/models';

export async function loadModelRegistry(): Promise<RegisteredModel[]> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(MODELS_API_URL, { headers });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload?.error || 'Failed to load models from API.');
    }

    return payload?.models || [];
  } catch (apiError: any) {
    console.warn('[modelRegistryService] Server models API failed, trying browser Supabase read:', apiError?.message || apiError);
  }

  // Fallback to Supabase direct select
  const { data, error } = await supabase
    .from('model_registry')
    .select('*')
    .order('provider')
    .order('model_id');

  if (error) {
    console.warn('[modelRegistryService] Failed to load models direct from Supabase:', error.message);
    throw error;
  }

  return (data || []) as RegisteredModel[];
}

export async function saveModel(model: RegisteredModel): Promise<RegisteredModel> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be logged in as an admin to manage models.');
  }

  const url = model.id ? `${MODELS_API_URL}/${model.id}` : MODELS_API_URL;
  const method = model.id ? 'PUT' : 'POST';

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(model),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Failed to save model.');
  }

  return payload.model;
}

export async function deleteModel(id: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be logged in as an admin to delete models.');
  }

  const response = await fetch(`${MODELS_API_URL}/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Failed to delete model.');
  }
}
